import React, { lazy, Suspense } from 'react';
import { noop } from 'lodash';
import {
    CustomOrderData,
    CustomOrderSubCategoryOption,
    useCurrentPageName,
    useLocalizationContext,
} from '@fiverr-private/seller_pages_toolbox';
import { useCustomOrderModal, useCustomOrderModalUpdater } from '../../../hooks/useCustomOrderModal';
import { useAgencySellerData } from '../../../hooks/useAgencySellerData';
import { AgencySeller } from '../../../types';

const LazyCustomOrderModal = lazy(
    () =>
        import(
            /* webpackChunkName: 'CustomOrderModal' */ '@fiverr-private/custom_order/src/entries/CustomOrderModalEntry'
        )
);

const categoriesDataSerializer = (data: CustomOrderData | null) =>
    data && data.dds
        ? data.dds.categories.options.map((item) => ({
              value: item.val,
              label: item.text,
              subCategories: data.dds.subCategories.options[item.val].map((subItem: CustomOrderSubCategoryOption) => ({
                  value: subItem.val,
                  label: subItem.text,
                  metadata: data.metaData[item.val] || [],
              })),
          }))
        : [];

export const CustomOrderModal = () => {
    const pageName = useCurrentPageName();
    const { currency } = useLocalizationContext();
    const modalData = useCustomOrderModal();
    const modalDataUpdater = useCustomOrderModalUpdater();
    const { username, displayName, profileImageUrl } = useAgencySellerData(selectSellerSlice);

    const handleClose = () => modalDataUpdater({ isOpen: false, data: null });

    if (!modalData.isOpen || !modalData.data) {
        return null;
    }

    const categoriesData = categoriesDataSerializer(modalData.data);

    return (
        <Suspense fallback={null}>
            <LazyCustomOrderModal
                autoOpen
                source={pageName}
                currency={currency}
                trigger={noop}
                onClose={handleClose}
                seller={{ name: username, image: profileImageUrl, displayName }}
                categoriesData={categoriesData}
                submitPath={modalData.data.submitPath}
                persistType={modalData.data.persistType}
                uploadOptions={modalData.data.attachmentsUploadConfig}
            />
        </Suspense>
    );
};

const selectSellerSlice = (seller: AgencySeller) => ({
    username: seller.user.name,
    displayName: seller.user.profile.displayName,
    profileImageUrl: seller.user.profileImageUrl,
});
